// Voice note for the daily log (V0.4.15): the PM talks through the day; the phone records it and writes it down as
// they speak (the browser's own speech-to-text, when it has one). SWI AI then fills the answers — each one shows the
// words it came from, and nothing is kept until the PM presses "Keep".
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import type { VoiceProposal } from '../../lib/daily';
import { Button, Drawer, errText, useToast } from '../kit';
import { C3Busy, C3Loader } from '../C3Loader';

type SR = { start(): void; stop(): void; abort(): void; continuous: boolean; interimResults: boolean; lang: string; onresult: ((e: { resultIndex: number; results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void) | null; onerror: ((e: { error: string }) => void) | null; onend: (() => void) | null };
const speechCtor = (): (new () => SR) | null => {
  const w = window as unknown as { SpeechRecognition?: new () => SR; webkitSpeechRecognition?: new () => SR };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
};

export type VoiceResult = { proposal: VoiceProposal; transcript: string; seconds: number; audio: Blob | null };

export function VoiceNote({ open, onClose, context, onResult }: { open: boolean; onClose: () => void; context: { site_id: string; log_date: string; buildings: { name: string; works: string[] }[]; orgs: string[] }; onResult: (r: VoiceResult) => void }) {
  const toast = useToast();
  const [state, setState] = useState<'idle' | 'rec' | 'thinking'>('idle');
  const [text, setText] = useState('');
  const [interim, setInterim] = useState('');
  const [secs, setSecs] = useState(0);
  const rec = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const sr = useRef<SR | null>(null);
  const t0 = useRef(0);
  const tick = useRef<ReturnType<typeof setInterval> | null>(null);
  const canSpeech = Boolean(speechCtor());

  useEffect(() => {
    if (!open) stopAll(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => () => stopAll(true), []); // eslint-disable-line react-hooks/exhaustive-deps

  function stopAll(discard = false) {
    if (tick.current) clearInterval(tick.current);
    tick.current = null;
    try {
      sr.current?.stop();
    } catch {
      /* already stopped */
    }
    sr.current = null;
    if (rec.current && rec.current.state !== 'inactive') {
      if (discard) rec.current.ondataavailable = null;
      rec.current.stop();
      rec.current.stream.getTracks().forEach((t) => t.stop());
    }
    setInterim('');
  }

  async function start() {
    chunks.current = [];
    try {
      if (navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined') {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const m = new MediaRecorder(stream);
        m.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
        m.start(1000);
        rec.current = m;
      }
    } catch {
      toast({ tone: 'error', text: 'The microphone is blocked — allow it in the browser, or type the notes below.' });
    }
    const C = speechCtor();
    if (C) {
      const r = new C();
      r.continuous = true;
      r.interimResults = true;
      r.lang = 'en-AU';
      r.onresult = (e) => {
        let fin = '';
        let mid = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const x = e.results[i]!;
          if (x.isFinal) fin += x[0].transcript + ' ';
          else mid += x[0].transcript;
        }
        if (fin) setText((t) => (t + ' ' + fin).replace(/\s+/g, ' ').trimStart());
        setInterim(mid);
      };
      r.onerror = () => {};
      r.onend = () => {
        // phones stop listening after a pause — carry on while still recording
        if (sr.current === r) {
          try {
            r.start();
          } catch {
            /* ignore */
          }
        }
      };
      sr.current = r;
      try {
        r.start();
      } catch {
        /* ignore */
      }
    }
    t0.current = Date.now();
    setSecs(0);
    tick.current = setInterval(() => setSecs(Math.round((Date.now() - t0.current) / 1000)), 500);
    setState('rec');
  }

  async function finish() {
    const seconds = Math.round((Date.now() - t0.current) / 1000) || secs;
    const mr = rec.current;
    const audio = await new Promise<Blob | null>((res) => {
      if (!mr || mr.state === 'inactive') return res(chunks.current.length ? new Blob(chunks.current, { type: chunks.current[0]!.type }) : null);
      mr.onstop = () => res(chunks.current.length ? new Blob(chunks.current, { type: chunks.current[0]!.type || 'audio/webm' }) : null);
      stopAll();
    });
    stopAll();
    rec.current = null;
    await think(seconds, audio);
  }

  async function think(seconds: number, audio: Blob | null) {
    const transcript = (text + ' ' + interim).trim();
    if (transcript.length < 10) {
      setState('idle');
      return toast({ tone: 'error', text: canSpeech ? 'Nothing was heard — try again closer to the phone, or type the notes.' : 'Type (or dictate with the keyboard microphone) what happened today, then press "Fill the log".' });
    }
    setState('thinking');
    try {
      const r = await api<{ proposal: VoiceProposal }>('daily-voice', { ...context, transcript });
      onResult({ proposal: r.proposal, transcript, seconds, audio });
      setText('');
      setState('idle');
    } catch (e) {
      setState('idle');
      toast({ tone: 'error', text: errText(e) });
    }
  }

  const mm = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
  return (
    <Drawer open={open} title="Talk me through the day" onClose={() => (state === 'rec' ? (stopAll(true), setState('idle'), onClose()) : onClose())}>
      <div className="dl-voice">
        <p className="muted small">Walk the site and talk: weather, barge and boat runs, who came, what each crew did, any hold-ups. SWI AI writes the summary and fills the answers — you check each one before it's kept.</p>
        <div className="dl-rec">
          {state === 'rec' ? (
            <>
              <span className="dl-wave" aria-hidden="true">{Array.from({ length: 12 }, (_, i) => <span key={i} style={{ animationDelay: `${(i % 6) * 0.12}s` }} />)}</span>
              <b>Recording · {mm}</b>
              <Button kind="primary" onClick={() => void finish()}>Stop and fill the log</Button>
            </>
          ) : state === 'thinking' ? (
            <>
              <C3Loader size={56} variant="levels" label="SWI AI is filling the log" />
              <b>SWI AI is filling the log…</b>
            </>
          ) : (
            <>
              <button type="button" className="dl-mic" aria-label="Start recording" onClick={() => void start()}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" aria-hidden="true"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></svg>
              </button>
              <span className="muted small">{canSpeech ? 'Tap to start — it writes down what you say as you talk' : 'This browser can record but not write it down — type below, or use the keyboard microphone'}</span>
            </>
          )}
        </div>
        <label className="field">
          <span className="field-label">What SWI AI heard {canSpeech ? '(fix anything it got wrong)' : ''}</span>
          <textarea rows={6} value={text + (interim ? ' ' + interim : '')} onChange={(e) => setText(e.target.value)} aria-label="Voice note text" placeholder="e.g. Showers after lunch, lost about an hour and a half outside. Barge in at 7:40 with the SQ2 roof sheets…" disabled={state === 'thinking'} />
        </label>
        {state === 'idle' && text.trim() ? (
          <Button kind="primary" onClick={() => void think(secs, null)}>{state === 'idle' ? 'Fill the log from this' : <C3Busy text="…" accent />}</Button>
        ) : null}
      </div>
    </Drawer>
  );
}
